// src/pages/VerifyPage.js
import React, { useEffect, useState } from 'react'
import { useParams, useNavigate } from 'react-router-dom'
import { supabase } from '../supabaseClient'
import PlanOverview from '../components/plan/PlanOverview'
import FinancialSummary from '../components/plan/FinancialSummary'

function VerifyPage() {
  const { rowId } = useParams()
  const navigate = useNavigate()
  const [plan, setPlan] = useState(null)
  const [editedPlan, setEditedPlan] = useState(null)
  const [loading, setLoading] = useState(true)

  // Fetch plan by id
  useEffect(() => {
    const fetchPlan = async () => {
      const { data, error } = await supabase
        .from('plans_test')
        .select('*')
        .eq('id', rowId)
        .single()

      if (error) {
        console.error('Error fetching plan:', error)
      } else {
        setPlan(data)
        setEditedPlan({
          ...data.parse_result?.parsed,
          group_name: data.group_name,
          group_id: data.group_id,
          insurance_company: data.insurance_company,
          benefit_year: data.benefit_year,
          customer_service_phone: data.customer_service_phone,
        })
      }
      setLoading(false)
    }

    fetchPlan()
  }, [rowId])

  const handleVerify = async () => {
    // 1) Get current auth user
    const { data: userData, error: userError } = await supabase.auth.getUser()
    console.log('auth.getUser result:', { userData, userError })

    if (userError) {
      console.error('Error fetching current user:', userError)
      alert('Could not find a logged-in user. Please log in again and try verifying the plan.')
      return
    }

    if (!userData?.user) {
      console.warn('No user found in auth.getUser() result')
      alert('No logged-in user found. Please log in again before verifying this plan.')
      return
    }

    const user = userData.user

    // 2) Lookup full_name from public.users where id = auth user id
    const { data: profile, error: profileError } = await supabase
      .from('users') // public.users table
      .select('id, full_name')
      .eq('id', user.id)
      .maybeSingle()

    console.log('public.users lookup:', { profile, profileError })

    if (profileError) {
      console.error('Error fetching profile from users table:', profileError)
    }

    const verifiedBy = profile?.full_name || user.email || 'Unknown User'
    console.log('Resolved verifiedBy:', verifiedBy)

    // 3) Save plan updates
    const { error } = await supabase
      .from('plans_test')
      .update({
        // Top-level updates
        group_name: editedPlan.group_name,
        group_id: editedPlan.group_id,
        insurance_company: editedPlan.insurance_company,

      

        // Store updated parsed data
        parse_result: { parsed: editedPlan },
        confidence_status: 'Verified',
        verified_by: verifiedBy,
        verified_date: new Date().toISOString(),
      })
      .eq('id', rowId)

    if (error) {
      console.error('Verification failed:', error)
      alert(`Verification failed: ${error.message}`)
    } else {
      navigate('/dashboard')
    }
  }

  const handleSkip = () => navigate('/dashboard')

  if (loading) return <p>Loading...</p>
  if (!plan || !editedPlan) return <p>Plan not found.</p>

  const parsed = editedPlan

  const categories = {
    Preventive: ['exams', 'bitewing_xray', 'Panoramic_xray', 'FMX_xray', 'fluoride', 'sealants', 'prophy'],
    Basic: ['fillings', 'endo', 'extractions', 'srp', 'periodontal', 'anesthesia', 'perio_maintenance'],
    Major: ['crowns', 'bridges', 'dentures', 'implants', 'core_buildup', 'inlays_onlays'],
    Surgery: ['oral_surgery', 'osseous_surgery'],
    Orthodontic: ['orthodontic'],
  }

  const renderCoverageByCategory = () =>
    Object.entries(categories).map(([category, procedures]) => {
      const items = procedures.filter((proc) => parsed.coverage?.[proc])
      if (items.length === 0) return null

      return (
        <div key={category} style={{ marginBottom: '1.25rem' }}>
          <h4 style={{ fontSize: '0.95rem', marginBottom: '0.5rem', fontWeight: 600 }}>{category}</h4>
          <div
            style={{
              display: 'grid',
              gridTemplateColumns: 'repeat(auto-fill, minmax(240px, 1fr))',
              gap: '0.75rem',
            }}
          >
            {items.map((proc) => {
              const item = parsed.coverage[proc]
              return (
                <div
                  key={proc}
                  style={{
                    background: '#f9fafb',
                    border: '1px solid #e2e8f0',
                    borderRadius: '6px',
                    padding: '0.65rem',
                    fontSize: '0.75rem',
                  }}
                >
                  <strong
                    style={{
                      textTransform: 'capitalize',
                      fontSize: '0.8rem',
                      display: 'block',
                      marginBottom: '0.4rem',
                    }}
                  >
                    {proc.replace(/_/g, ' ')}
                  </strong>

                  <label style={{ display: 'block', marginBottom: '0.2rem', fontSize: '0.7rem' }}>
                    Coverage % / Copay
                  </label>
                  <input
                    type="text"
                    value={item.coverage_percent || ''}
                    onChange={(e) =>
                      setEditedPlan((prev) => ({
                        ...prev,
                        coverage: {
                          ...prev.coverage,
                          [proc]: {
                            ...prev.coverage[proc],
                            coverage_percent: e.target.value,
                          },
                        },
                      }))
                    }
                    style={{
                      width: '80px',
                      padding: '0.25rem',
                      fontSize: '0.75rem',
                      borderRadius: '4px',
                      border: '1px solid #ccc',
                    }}
                  />

                  <label
                    style={{
                      display: 'block',
                      marginTop: '0.5rem',
                      marginBottom: '0.2rem',
                      fontSize: '0.7rem',
                    }}
                  >
                    Notes
                  </label>
                  <textarea
                    value={item.notes || ''}
                    onChange={(e) =>
                      setEditedPlan((prev) => ({
                        ...prev,
                        coverage: {
                          ...prev.coverage,
                          [proc]: {
                            ...prev.coverage[proc],
                            notes: e.target.value,
                          },
                        },
                      }))
                    }
                    style={{
                      width: '100%',
                      padding: '0.35rem',
                      fontSize: '0.75rem',
                      borderRadius: '4px',
                      border: '1px solid #ccc',
                      minHeight: '60px',
                      resize: 'vertical',
                    }}
                  />
                </div>
              )
            })}
          </div>
        </div>
      )
    })

  return (
    <div style={{ padding: '1.5rem', maxWidth: '1400px', margin: '0 auto', fontFamily: 'system-ui' }}>
      <h2 style={{ fontSize: '1.2rem', fontWeight: 700 }}>✅ Plan Verification</h2>

      {plan.confidence_status === 'Verified' && plan.verified_date && (
        <p style={{ color: 'green', fontWeight: 600 }}>
          ✅ Already verified by {plan.verified_by} on {new Date(plan.verified_date).toLocaleDateString()}.
        </p>
      )}

      {/* Editable Plan Overview & Financial Summary */}
      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '1rem', marginTop: '1rem' }}>
        <PlanOverview plan={parsed} setEditedPlan={setEditedPlan} editable={true} />
        <FinancialSummary plan={parsed} setEditedPlan={setEditedPlan} editable={true} />
      </div>

      {/* Coverage Breakdown */}
      <div style={{ marginTop: '2rem' }}>
        <h3 style={{ fontSize: '1.1rem', marginBottom: '0.75rem' }}>🦷 Coverage Breakdown</h3>
        {renderCoverageByCategory()}
      </div>

      {/* Waiting Periods & Flags */}
      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '1rem', marginTop: '2rem' }}>
        {/* Waiting Periods */}
        <div>
          <h3 style={{ fontSize: '1rem', marginBottom: '0.5rem' }}>⏳ Waiting Periods</h3>
          {parsed.waiting_periods && Object.keys(parsed.waiting_periods).length > 0 ? (
            Object.entries(parsed.waiting_periods).map(([key, value]) => (
              <div key={key} style={{ marginBottom: '0.5rem' }}>
                <label style={{ display: 'block', fontWeight: 600, marginBottom: '0.25rem' }}>
                  {key.replace(/_/g, ' ')}
                </label>
                <input
                  type="text"
                  value={value || ''}
                  onChange={(e) =>
                    setEditedPlan((prev) => ({
                      ...prev,
                      waiting_periods: { ...prev.waiting_periods, [key]: e.target.value },
                    }))
                  }
                  style={{
                    width: '100%',
                    padding: '0.4rem',
                    fontSize: '0.85rem',
                    borderRadius: '4px',
                    border: '1px solid #ccc',
                  }}
                />
              </div>
            ))
          ) : (
            <p>None</p>
          )}
        </div>

        {/* Plan Flags */}
        <div>
          <h3 style={{ fontSize: '1rem', marginBottom: '0.5rem' }}>🚩 Plan Flags</h3>
          {parsed.flags && Object.keys(parsed.flags).length > 0 ? (
            Object.entries(parsed.flags).map(([key, value]) => (
              <div key={key} style={{ marginBottom: '0.5rem' }}>
                <label style={{ display: 'block', fontWeight: 600, marginBottom: '0.25rem' }}>
                  {key.replace(/_/g, ' ')}
                </label>
                <input
                  type="text"
                  value={value || ''}
                  onChange={(e) =>
                    setEditedPlan((prev) => ({
                      ...prev,
                      flags: { ...prev.flags, [key]: e.target.value },
                    }))
                  }
                  style={{
                    width: '100%',
                    padding: '0.4rem',
                    fontSize: '0.85rem',
                    borderRadius: '4px',
                    border: '1px solid #ccc',
                  }}
                />
              </div>
            ))
          ) : (
            <p>No flags</p>
          )}
        </div>
      </div>

      {/* Disclaimers */}
      <div style={{ marginTop: '2rem' }}>
        <h3 style={{ fontSize: '1rem', marginBottom: '0.5rem' }}>⚠️ Disclaimers</h3>
        {(parsed.disclaimers || []).map((d, i) => (
          <div key={i} style={{ marginBottom: '0.75rem' }}>
            <textarea
              value={d}
              onChange={(e) => {
                const updated = [...(parsed.disclaimers || [])]
                updated[i] = e.target.value
                setEditedPlan((prev) => ({ ...prev, disclaimers: updated }))
              }}
              style={{
                width: '100%',
                padding: '0.6rem',
                fontSize: '0.85rem',
                borderRadius: '4px',
                border: '1px solid #ccc',
              }}
            />
          </div>
        ))}
      </div>

      {/* Actions */}
      <div style={{ display: 'flex', gap: '1rem', marginTop: '2rem' }}>
        <button
          onClick={handleVerify}
          style={{
            padding: '0.75rem 1.25rem',
            backgroundColor: '#0f172a',
            color: 'white',
            border: 'none',
            borderRadius: '5px',
            fontWeight: 600,
            fontSize: '0.9rem',
            cursor: 'pointer',
          }}
        >
          ✅ Confirm This Plan
        </button>
        <button
          onClick={handleSkip}
          style={{
            padding: '0.75rem 1.25rem',
            backgroundColor: '#6c757d',
            color: 'white',
            border: 'none',
            borderRadius: '5px',
            fontWeight: 600,
            fontSize: '0.9rem',
            cursor: 'pointer',
          }}
        >
          ⏭️ Skip for Now
        </button>
      </div>
    </div>
  )
}

export default VerifyPage
